import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Edit, X, Save, User, CalendarPlus, PhoneOff, ArrowLeft, StickyNote, UserCircle, Stethoscope, History, BarChart3, FolderOpen, Activity, AlertTriangle } from 'lucide-react';
import { authService } from '../../auth/services/authService';
import { patientService } from '../../patient/services/patientService';
import { clinicianService } from '../services/clinicianService';
import toast from 'react-hot-toast';
import GeneralDetailsContent from '../components/PatientFileContents/GeneralDetailsContent';
import MedicalDetailsContent from '../components/PatientFileContents/MedicalDetailsContent';
import HistoryContent from '../components/PatientFileContents/HistoryContent';
import AnalyticsContent from '../components/PatientFileContents/AnalyticsContent';
import DocumentsContent from '../components/PatientFileContents/DocumentsContent';
import ExactBookingForm from '../../doctor/components/ExactBookingForm';
// Temporary: Use new window mode until VisionFlex whitelists https://app.medifusionpro.io/
// Switch back to VisionFlexPlayer once domain is whitelisted by VisionFlex
import VisionFlexPlayer from '../../../components/VideoCall/VisionFlexPlayer';
import DiagnosticsPanel from '../../../components/Diagnostics/DiagnosticsPanel';
import ECGUploadModal from '../../../components/Diagnostics/ECGUploadModal';
import SpirometryUploadModal from '../../../components/Diagnostics/SpirometryUploadModal';

const ClinicianClinicalTelehealthNotesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { booking_id } = useParams(); // Get booking ID from URL
  const appointmentData = location.state?.appointmentData; // Get appointment data from navigation state
  const currentUser = authService.getCurrentUser();
  const selectedRole = localStorage.getItem('selectedRole');
  
  // State for validation
  const [validatedAppointmentData, setValidatedAppointmentData] = useState(null);
  const [loadingValidation, setLoadingValidation] = useState(true);
  const [validationError, setValidationError] = useState(null);
  
  // State for appointment and patient data
  const [appointmentDetails, setAppointmentDetails] = useState({
    doctorName: '',
    clinicianName: '',
    patientId: '',
    patientName: ''
  });
  const [patientDetails, setPatientDetails] = useState({
    age: '',
    weight: '',
    bmi: '',
    knownConditions: ''
  });
  
  // Get patient ID from appointment data or fallback
  const patientId = validatedAppointmentData?.patient_id || '';
  
  // State for active tab
  const [activeTab, setActiveTab] = useState('Notes');
  
  // State for notes content
  const [doctorNotes, setDoctorNotes] = useState('');
  const [patientNotes, setPatientNotes] = useState('');
  const [clinicianNotes, setClinicianNotes] = useState('');
  
  // State for ECG Upload Modal
  const [isECGUploadModalOpen, setIsECGUploadModalOpen] = useState(false);
  
  // State for Spirometry Upload Modal
  const [isSpirometryUploadModalOpen, setIsSpirometryUploadModalOpen] = useState(false);
  
  // Note: Clinicians cannot mark bookings as complete - only doctors can
  
  // State for video size and panel visibility (for smooth animations)
  const [videoSize, setVideoSize] = useState('wide');
  const [showRightPanel, setShowRightPanel] = useState(true);
  
  // State for editing functionality
  const [isEditing, setIsEditing] = useState(false);
  const [backupNotes, setBackupNotes] = useState({
    doctorNotes: '',
    patientNotes: '',
    clinicianNotes: ''
  });

  // State for booking form
  const [showBookingForm, setShowBookingForm] = useState(false);

  // Handle video call end
  const handleCallEnd = () => {
    console.log('🔚 Clinician video call ended - navigating to dashboard');
    
    // Dismiss any existing toasts to prevent overlap
    toast.dismiss();
    
    // Navigate directly to dashboard (clinicians cannot mark bookings as complete)
    navigate('/clinician/dashboard');
  };

  // Handle video size change from VisionFlexPlayer
  const handleVideoSizeChange = (newSize) => {
    console.log('🎬 Video size changed to:', newSize);
    setVideoSize(newSize);
    
    // Auto-hide right panel when going to fullscreen
    if (newSize === 'fullscreen') {
      setShowRightPanel(false);
    } else if (!showRightPanel && newSize === 'wide') {
      // Auto-show panel when manually switching to wide from fullscreen
      setShowRightPanel(true);
    }
  };

  // Toggle right panel visibility
  const toggleRightPanel = () => {
    if (!showRightPanel && videoSize === 'fullscreen') {
      // If opening panel from fullscreen, switch to wide size
      setVideoSize('wide');
      setShowRightPanel(true);
      // Note: VisionFlexPlayer will be re-rendered with new size via the handleSizeChange callback
    } else {
      setShowRightPanel(!showRightPanel);
    }
  };

  // Validate booking access and get appointment data
  const validateBookingAccess = async () => {
    try {
      setLoadingValidation(true);
      setValidationError(null);
      
      if (!booking_id) {
        throw new Error('No booking ID provided');
      }
      
      // If appointment data is provided via state, validate it matches the booking ID
      if (appointmentData && appointmentData.booking_id === parseInt(booking_id)) {
        setValidatedAppointmentData(appointmentData);
        setLoadingValidation(false);
        return;
      }
      
      // Fetch booking data from backend to validate clinician access
      try {
        // Get all bookings that the clinician can access (from their associated clinics)
        const clinicianBookings = await clinicianService.getClinicianBookings();
        const booking = clinicianBookings.find(b => b.id === parseInt(booking_id));
        
        if (!booking) {
          throw new Error('Booking not found or you do not have access to this appointment');
        }
        
        // Validate that this is a clinical telehealth appointment
        if (booking.appointment_type !== 'clinical_telehealth' && booking.appointment_type !== 'Clinical Telehealth') {
          throw new Error('Clinicians can only access clinical telehealth appointments');
        }
        
        // Create appointment data from booking
        const validatedData = {
          booking_id: booking.id,
          patient_id: booking.patient_id,
          doctor_id: booking.doctor_id,
          appointment_type: booking.appointment_type,
          appointment_date: booking.appointment_date,
          appointment_time: booking.appointment_time,
          patient_name: booking.patient_name,
          doctor_name: booking.doctor,
          clinician_name: booking.clinician_name,
          reason: booking.reason,
          specialty: booking.specialty,
          clinic_location: booking.clinic_location,
          schedule_type: booking.schedule_type,
          status: booking.status
        };
        
        setValidatedAppointmentData(validatedData);
        console.log('✅ Booking access validated for clinician:', currentUser.id, 'booking:', booking_id);
        
      } catch (apiError) {
        // If clinicianService.getClinicianBookings() doesn't exist yet, 
        // fallback to a basic validation approach
        console.warn('Clinician booking service not available, using basic validation');
        
        if (appointmentData) {
          setValidatedAppointmentData(appointmentData);
        } else {
          throw new Error('Cannot validate booking access. Please access from your dashboard.');
        }
      }
      
    } catch (error) {
      console.error('❌ Booking validation failed:', error);
      setValidationError(error.message);
      toast.error(error.message);
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/clinician/dashboard');
      }, 3000);
    } finally {
      setLoadingValidation(false);
    }
  };

  // Validate booking access on component mount
  useEffect(() => {
    validateBookingAccess();
  }, [booking_id, currentUser.id]);

  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Clinician Clinical Telehealth Notes';
  }, []);

  // Load existing notes when component mounts or booking changes
  useEffect(() => {
    const loadExistingNotes = async () => {
      try {
        const bookingId = validatedAppointmentData?.booking_id || booking_id;
        if (!bookingId) return;
        
        // Load notes from backend API
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
        const response = await fetch(`${apiBaseUrl}/appointment-notes/${bookingId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const notesData = await response.json();
          setDoctorNotes(notesData.doctor_notes || '');
          setPatientNotes(notesData.patient_notes || '');
          setClinicianNotes(notesData.clinician_notes || '');
          console.log('📥 Loaded notes from database:', notesData);
        }
        
      } catch (error) {
        console.error('Error loading existing notes:', error);
      }
    };
    
    loadExistingNotes();
  }, [validatedAppointmentData?.booking_id, booking_id]);
  
  // Store current user's full name to avoid useEffect dependency issues
  const currentUserName = currentUser?.full_name || `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Current Clinician';
  
  // Debug current user
  console.log('🔍 Debug currentUser:', currentUser);
  console.log('🔍 Debug currentUserName:', currentUserName);

  // Load appointment and patient data when component mounts
  useEffect(() => {
    const loadAppointmentData = async () => {
      if (validatedAppointmentData) {
        console.log('📋 Loading appointment data:', validatedAppointmentData);
        console.log('🔍 Debug - Available fields:', Object.keys(validatedAppointmentData));
        console.log('🔍 Debug - doctor_name:', validatedAppointmentData.doctor_name);
        console.log('🔍 Debug - clinician_name:', validatedAppointmentData.clinician_name);
        console.log('🔍 Debug - clinic_location:', validatedAppointmentData.clinic_location);
        
        // Extract doctor name from appointment data
        let doctorName = validatedAppointmentData.doctor_name || 'TBD';
        
        // For clinician name, use the current logged-in clinician (who is viewing this page)
        let clinicianName = currentUserName;
        
        // Override if appointment data explicitly provides clinician name
        if (validatedAppointmentData.clinician_name) {
          clinicianName = validatedAppointmentData.clinician_name;
        }
        
        console.log('🎯 Final values - Doctor:', doctorName, 'Clinician:', clinicianName);
        
        // Update appointment details from the passed data
        const finalAppointmentDetails = {
          doctorName: doctorName,
          clinicianName: clinicianName,
          patientId: validatedAppointmentData.patient_id || 'TBD',
          patientName: validatedAppointmentData.patient_name || 'TBD'
        };
        
        console.log('📋 Setting appointment details:', finalAppointmentDetails);
        setAppointmentDetails(finalAppointmentDetails);

        // Populate patient notes with appointment reason if available
        if (validatedAppointmentData.reason && validatedAppointmentData.reason.trim()) {
          console.log('📝 Populating patient notes with appointment reason:', validatedAppointmentData.reason);
          setPatientNotes(validatedAppointmentData.reason.trim());
        }
        
        // Load additional patient data if needed
        if (validatedAppointmentData.patient_id) {
          try {
            console.log('👤 Fetching patient details for ID:', validatedAppointmentData.patient_id);
            const patientData = await patientService.getPatientByPatientId(validatedAppointmentData.patient_id);
            console.log('👤 Patient data loaded:', patientData);
            
            // Calculate age from date of birth
            const calculateAge = (dob) => {
              if (!dob) return 'Unknown';
              const birthDate = new Date(dob);
              const today = new Date();
              let age = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();
              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
              }
              return age.toString();
            };
            
            setPatientDetails({
              age: calculateAge(patientData.dob),
              weight: patientData.weight ? `${patientData.weight}kg` : 'Unknown',
              bmi: patientData.height && patientData.weight ? 
                (patientData.weight / Math.pow(patientData.height / 100, 2)).toFixed(1) : 'Unknown',
              knownConditions: patientData.medical_conditions || 'None recorded'
            });
          } catch (error) {
            console.error('Error loading patient data:', error);
            // Keep default values if loading fails
          }
        }
      } else {
        console.warn('⚠️ No appointment data provided to clinical telenotes page');
        // If no appointment data is provided, show guidance
        setAppointmentDetails({
          doctorName: 'No appointment data',
          clinicianName: currentUserName,
          patientId: 'No patient selected',
          patientName: 'No patient selected'
        });
      }
    };
    
    loadAppointmentData();
  }, [validatedAppointmentData, currentUserName]);

  // Note: Clinicians don't need beforeunload handling since they can't complete bookings

  // Handle edit button click
  const handleEditRecord = () => {
    setIsEditing(true);
    // Backup current notes for cancel functionality
    setBackupNotes({
      doctorNotes: doctorNotes,
      patientNotes: patientNotes,
      clinicianNotes: clinicianNotes
    });
  };

  // Check if this is a clinical telehealth appointment
  const isClinicalTelehealth = true; // Always true for clinical telehealth notes page

  // Handle save changes
  const handleSaveChanges = async () => {
    try {
      setIsEditing(false);
      
      // Get booking ID for saving notes
      const bookingId = validatedAppointmentData?.booking_id || booking_id;
      if (!bookingId) {
        toast.error('Unable to save notes: No booking ID available');
        return;
      }
      
      // Get patient ID - try multiple sources
      let patientIdForNotes = validatedAppointmentData?.patient_id || 
                               appointmentData?.patient_id || 
                               validatedAppointmentData?.id;
      
      if (!patientIdForNotes) {
        toast.error('Unable to save notes: No patient ID available');
        return;
      }
      
      // Prepare notes data
      const notesData = {
        booking_id: bookingId,
        patient_id: patientIdForNotes,
        doctor_notes: doctorNotes.trim(),
        patient_notes: patientNotes.trim(),
        clinician_notes: clinicianNotes.trim(),
        appointment_type: validatedAppointmentData?.appointment_type || 'clinical_telehealth',
        author_user_id: currentUser?.id,
        author_name: `${currentUser?.first_name} ${currentUser?.last_name}`,
        author_role: 'Clinician'
      };
      
      console.log('💾 Saving notes to database:', notesData);
      
      // Save notes to backend API
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${apiBaseUrl}/appointment-notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notesData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save notes');
      }
      
      toast.success('Notes saved successfully!');
      
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes. Please try again.');
      setIsEditing(true); // Re-enable editing on error
    }
  };

  // Handle cancel changes
  const handleCancelChanges = () => {
    setIsEditing(false);
    // Restore backup notes
    setDoctorNotes(backupNotes.doctorNotes);
    setPatientNotes(backupNotes.patientNotes);
    setClinicianNotes(backupNotes.clinicianNotes);
  };

  // Note: Clinicians cannot complete bookings - only doctors can

  // Note: Clinicians don't need page unload confirmation since they can't complete bookings

  // Note: Clinicians don't need navigation confirmation since they can't complete bookings

  // Show loading while validating booking access
  if (loadingValidation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating appointment access...</p>
        </div>
      </div>
    );
  }

  // Show error if validation failed
  if (validationError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white p-8 rounded-lg shadow-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6m0 0l6 6m-6-6l6-6"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">{validationError}</p>
          <p className="text-sm text-gray-500 mb-4">Redirecting to your dashboard...</p>
          <button
            onClick={() => navigate('/clinician/dashboard')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show loading if no validated appointment data yet
  if (!validatedAppointmentData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading appointment details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ zoom: '90%' }}>
      {/* Header - Same as DashboardLayout but without Navigation */}
      <div className="bg-white shadow-sm mb-6">
        {/* Top Header - Logos and User */}
        <div className="flex justify-between items-center px-6 py-2">
          {/* Left side - MediFusion Logo */}
          <div className="flex items-center">
            <img
              src="/login_medi_logo.png"
              alt="MediFusion"
              className="h-10 w-auto"
            />
          </div>
          
          {/* User Name and GARLI MEDI TECH Logo - Right */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2 mr-10">
              <User className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">
                {currentUser?.first_name} {currentUser?.last_name}
              </span>
            </div>
            <img
              src="/garlimeditech.png"
              alt="GARLI MEDI TECH"
              className="h-8 w-auto"
            />
          </div>
        </div>
      </div>
      {/* Video and Content Division - Flexible sizing for VisionFlex with sliding animation */}
      <div className="flex gap-4 px-4 pl-6 relative">
        {/* Left Side - VisionFlex Video Call Component (auto-sizing based on user selection) */}
        <div className="flex-shrink-0">
          <VisionFlexPlayer
            bookingId={validatedAppointmentData?.id}
            userRole="clinician"
            appointmentData={validatedAppointmentData}
            onCallEnd={handleCallEnd}
            onVideoSizeChange={handleVideoSizeChange}
            videoSize={videoSize}
            isEmbedded={true}
            className="rounded-lg"
          />
        </div>

        {/* Toggle Button - Shows when right panel is hidden */}
        {!showRightPanel && (
          <div className="fixed top-1/2 right-0 z-50 transform -translate-y-1/2">
            <button
              onClick={toggleRightPanel}
              className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-4 py-6 rounded-l-xl shadow-2xl transition-all duration-300 hover:pr-6 flex flex-col items-center gap-3 group border-l-4 border-blue-400"
              title="Show appointment details and switch to wide view"
            >
              <div className="flex flex-col items-center gap-2">
                <span className="text-sm font-bold tracking-wide" style={{ writingMode: 'vertical-rl' }}>
                  Details
                </span>
              </div>
              <svg className="w-6 h-6 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
        )}

        {/* Right Side - Forms and Details with Smooth Slide Animation */}
        <div 
          className={`flex-1 p-4 space-y-4 overflow-y-auto transition-all duration-500 ease-in-out transform ${
            showRightPanel 
              ? 'translate-x-0 opacity-100' 
              : 'translate-x-full opacity-0 pointer-events-none absolute right-0'
          }`}
          style={{
            width: showRightPanel ? 'auto' : '0'
          }}
        >
          {/* Appointment Details */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h3 className="text-blue-800 font-semibold mb-2 text-sm">Appointment Details</h3>
            <div className="flex gap-3">
              {/* Left Side - 2x2 Grid of Fields */}
              <div className="flex-1">
                <div className="grid grid-cols-2 gap-2">
                  {/* First Row */}
                  <div>
                    <label className="block text-xs text-gray-700 mb-1">Doctor Name</label>
                    <input
                      type="text"
                      value={appointmentDetails.doctorName}
                      readOnly
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-700 mb-1">Clinician Name</label>
                    <input
                      type="text"
                      value={appointmentDetails.clinicianName}
                      readOnly
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-gray-50"
                    />
                  </div>
                  {/* Second Row */}
                  <div>
                    <label className="block text-xs text-gray-700 mb-1">Patient ID</label>
                    <input
                      type="text"
                      value={appointmentDetails.patientId}
                      readOnly
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-700 mb-1">Patient Name</label>
                    <input
                      type="text"
                      value={appointmentDetails.patientName}
                      readOnly
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-gray-50"
                    />
                  </div>
                </div>
              </div>
              
              {/* Right Side - Action Buttons */}
              <div className="flex flex-col gap-3 justify-end">
                <button 
                  onClick={() => setShowBookingForm(true)}
                  className={`px-4 py-3 rounded-lg text-xs font-medium transition-all duration-300 whitespace-nowrap flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-105 ${
                    showBookingForm 
                      ? 'bg-blue-600 text-white border-2 border-blue-700 shadow-lg scale-105' 
                      : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                >
                  <CalendarPlus className="h-4 w-4" />
                  <span>{showBookingForm ? 'Booking Active' : 'Book Follow Up'}</span>
                </button>
                <button 
                  onClick={handleCallEnd}
                  className="bg-red-500 text-white px-4 py-3 rounded-lg text-xs font-medium hover:bg-red-600 transition-all duration-300 whitespace-nowrap flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-105 hover:rotate-1"
                >
                  <PhoneOff className="h-4 w-4" />
                  <span>End Call</span>
                </button>
              </div>
            </div>
          </div>

          {/* Patient Details */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <h3 className="text-green-800 font-semibold mb-2 text-sm">Patient Details</h3>
            <div className="flex gap-2">
              {/* Age - 20% */}
              <div className="w-1/5">
                <label className="block text-xs text-gray-700 mb-1">Patient Age</label>
                <input
                  type="text"
                  value={patientDetails.age}
                  readOnly
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500 bg-gray-50"
                />
              </div>
              {/* Weight - 20% */}
              <div className="w-1/5">
                <label className="block text-xs text-gray-700 mb-1">Patient Weight</label>
                <input
                  type="text"
                  value={patientDetails.weight}
                  readOnly
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500 bg-gray-50"
                />
              </div>
              {/* BMI - 20% */}
              <div className="w-1/5">
                <label className="block text-xs text-gray-700 mb-1">Patient BMI</label>
                <input
                  type="text"
                  value={patientDetails.bmi}
                  readOnly
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500 bg-gray-50"
                />
              </div>
              {/* Known Conditions - 40% */}
              <div className="w-2/5">
                <label className="block text-xs text-gray-700 mb-1">Patient Known Conditions</label>
                <input
                  type="text"
                  value={patientDetails.knownConditions}
                  readOnly
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500 bg-gray-50"
                />
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className={`bg-purple-50 border border-purple-200 rounded-lg p-3 transition-all ${videoSize === 'wide' ? 'h-auto' : ''}`}>
            <h3 className="text-purple-800 font-semibold mb-2 text-sm">Quick Links</h3>
            <div className={`grid gap-2 ${videoSize === 'wide' ? 'grid-cols-3' : 'grid-cols-5'}`}>
              <button className="bg-blue-50 border-2 border-blue-500 rounded py-1.5 px-2 text-xs font-medium text-gray-900 hover:bg-blue-500 hover:text-white transition-all hover:shadow-md">
                Req. Pathology
              </button>
              <button className="bg-green-50 border-2 border-green-500 rounded py-1.5 px-2 text-xs font-medium text-gray-900 hover:bg-green-500 hover:text-white transition-all hover:shadow-md">
                Req. Imaging
              </button>
              <button 
                onClick={() => setIsECGUploadModalOpen(true)}
                className="bg-purple-50 border-2 border-purple-500 rounded py-1.5 px-2 text-xs font-medium text-gray-900 hover:bg-purple-500 hover:text-white transition-all hover:shadow-md"
              >
                Req. ECG
              </button>
              <button 
                onClick={() => setIsSpirometryUploadModalOpen(true)}
                className="bg-teal-50 border-2 border-teal-500 rounded py-1.5 px-2 text-xs font-medium text-gray-900 hover:bg-teal-500 hover:text-white transition-all hover:shadow-md"
              >
                Req. Spirometry
              </button>
              <button className="bg-orange-50 border-2 border-orange-500 rounded py-1.5 px-2 text-xs font-medium text-gray-900 hover:bg-orange-500 hover:text-white transition-all hover:shadow-md">
                Referral
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom 20-80 Division */}
      <div className="flex py-6 px-4 pl-6 gap-6">
        {/* Left Navigation Sidebar - 20% */}
        <div className="w-1/5">
          <div className="bg-white rounded-lg shadow-md p-4">
            <nav className="space-y-2">
              <button 
                onClick={() => {
                  setActiveTab('Notes');
                  setShowBookingForm(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                  activeTab === 'Notes' && !showBookingForm
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <StickyNote className="w-5 h-5" />
                <span>Notes</span>
              </button>
              <button 
                onClick={() => {
                  setActiveTab('General Details');
                  setShowBookingForm(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                  activeTab === 'General Details' && !showBookingForm
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <UserCircle className="w-5 h-5" />
                <span>General Details</span>
              </button>
              <button 
                onClick={() => {
                  setActiveTab('Medical Details');
                  setShowBookingForm(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                  activeTab === 'Medical Details' && !showBookingForm
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Stethoscope className="w-5 h-5" />
                <span>Medical Details</span>
              </button>
              <button 
                onClick={() => {
                  setActiveTab('History');
                  setShowBookingForm(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                  activeTab === 'History' && !showBookingForm
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <History className="w-5 h-5" />
                <span>History</span>
              </button>
              <button 
                onClick={() => {
                  setActiveTab('Analytics');
                  setShowBookingForm(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                  activeTab === 'Analytics' && !showBookingForm
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                <span>Analytics</span>
              </button>
              <button 
                onClick={() => {
                  setActiveTab('Documents');
                  setShowBookingForm(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                  activeTab === 'Documents' && !showBookingForm
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <FolderOpen className="w-5 h-5" />
                <span>Documents</span>
              </button>
              <button 
                onClick={() => {
                  setActiveTab('Diagnostics');
                  setShowBookingForm(false);
                }}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                  activeTab === 'Diagnostics' && !showBookingForm
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Activity className="w-5 h-5" />
                <span>Diagnostics</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Right Content Area - 80% */}
        <div className="flex-1">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            {/* Booking Form */}
            {showBookingForm && (
              <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg shadow-inner">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-blue-800 flex items-center space-x-2">
                    <CalendarPlus className="h-5 w-5" />
                    <span>Follow-up Booking</span>
                  </h3>
                  <button 
                    onClick={() => setShowBookingForm(false)}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <ExactBookingForm 
                  patientId={patientId}
                  onClose={() => setShowBookingForm(false)}
                />
              </div>
            )}

            {/* Notes Content */}
            {!showBookingForm && activeTab === 'Notes' && (
              <div className="p-6">
                <div className="space-y-6">
                  {/* Doctor Notes */}
                  <div>
                    <label className="block text-sm font-medium text-blue-600 mb-2">Doctor Notes:</label>
                    <input
                      type="text"
                      value={doctorNotes}
                      onChange={(e) => setDoctorNotes(e.target.value)}
                      placeholder="Enter doctor notes here..."
                      readOnly={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isEditing ? 'bg-white' : 'bg-gray-50'
                      }`}
                    />
                  </div>

                  {/* Patient Notes */}
                  <div>
                    <label className="block text-sm font-medium text-blue-600 mb-2">Patient Notes:</label>
                    <input
                      type="text"
                      value={patientNotes}
                      onChange={(e) => setPatientNotes(e.target.value)}
                      placeholder="Enter patient notes here..."
                      readOnly={!isEditing}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isEditing ? 'bg-white' : 'bg-gray-50'
                      }`}
                    />
                  </div>

                  {/* Clinician Notes - Always show for Clinical Telehealth */}
                  {isClinicalTelehealth && (
                    <div>
                      <label className="block text-sm font-medium text-blue-600 mb-2">Clinician Notes:</label>
                      <input
                        type="text"
                        value={clinicianNotes}
                        onChange={(e) => setClinicianNotes(e.target.value)}
                        placeholder="Enter clinician notes here..."
                        readOnly={!isEditing}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          isEditing ? 'bg-white' : 'bg-gray-50'
                        }`}
                      />
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-2 pt-4">
                    {!isEditing ? (
                      <button 
                        onClick={handleEditRecord}
                        className="bg-blue-600 text-white border border-blue-600 px-2 py-0.5 rounded-md hover:bg-white hover:text-blue-600 transition-colors flex items-center space-x-1 text-sm"
                      >
                        <Edit className="h-4 w-4" />
                        <span>Edit Record</span>
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handleCancelChanges}
                          className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                        >
                          <X className="h-4 w-4" />
                          <span>Cancel</span>
                        </button>
                        <button
                          onClick={handleSaveChanges}
                          className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                        >
                          <Save className="h-4 w-4" />
                          <span>Save</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* General Details Tab */}
            {!showBookingForm && activeTab === 'General Details' && (
              <div className="p-6">
                <GeneralDetailsContent patientId={patientId} />
              </div>
            )}

            {/* Medical Details Tab */}
            {!showBookingForm && activeTab === 'Medical Details' && (
              <div className="p-6">
                <MedicalDetailsContent patientId={patientId} />
              </div>
            )}

            {/* History Tab */}
            {!showBookingForm && activeTab === 'History' && (
              <div className="p-6">
                <HistoryContent patientId={patientId} />
              </div>
            )}

            {/* Analytics Tab */}
            {!showBookingForm && activeTab === 'Analytics' && (
              <div className="p-6">
                <AnalyticsContent patientId={patientId} />
              </div>
            )}

            {/* Documents Tab */}
            {!showBookingForm && activeTab === 'Documents' && (
              <div className="p-6">
                <DocumentsContent patientId={patientId} />
              </div>
            )}

            {/* Diagnostics Tab Content */}
            {!showBookingForm && activeTab === 'Diagnostics' && (
              <DiagnosticsPanel 
                bookingId={booking_id} 
                patientId={patientId}
              />
            )}
          </div>
        </div>
      </div>

      {/* ECG Upload Modal */}
      <ECGUploadModal
        isOpen={isECGUploadModalOpen}
        onClose={() => setIsECGUploadModalOpen(false)}
        bookingId={booking_id}
        patientName={validatedAppointmentData?.patient_name || patientDetails?.patientName || 'Unknown Patient'}
        onUploadSuccess={() => {
          // Trigger refresh of Diagnostics panel if active
          console.log('ECG uploaded successfully - diagnostics should refresh');
        }}
      />

      {/* Spirometry Upload Modal */}
      <SpirometryUploadModal
        isOpen={isSpirometryUploadModalOpen}
        onClose={() => setIsSpirometryUploadModalOpen(false)}
        bookingId={booking_id}
        patientName={validatedAppointmentData?.patient_name || patientDetails?.patientName || 'Unknown Patient'}
        onUploadSuccess={() => {
          // Trigger refresh of Diagnostics panel if active
          console.log('Spirometry uploaded successfully - diagnostics should refresh');
        }}
      />

      {/* Note: Clinicians cannot complete bookings - only doctors can */}
    </div>
  );
};

export default ClinicianClinicalTelehealthNotesPage;